/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/sharedlib.ts
 */

// Provide a way for sharedlib to fetch its environment-specific implementation
import type { RecordingController } from '@repo/common/types/RecordController';
import type { SharedLibHandler } from '@repo/common/types/SharedLib';

let handler: SharedLibHandler | null = null;
let handlerPromiseResolve: ((handler: SharedLibHandler) => void) | null = null;
let handlerPromise: Promise<SharedLibHandler> | null = null;

export function setHandler(newHandler: SharedLibHandler): void {
  handler = newHandler;
  if (handlerPromiseResolve) {
    handlerPromiseResolve(newHandler);
    handlerPromiseResolve = null;
    handlerPromise = null;
  }
}

export function getHandler(): SharedLibHandler {
  if (!handler) {
    throw new Error('Handler not set for sharedlib');
  }
  return handler;
}

export async function getHandlerAsync(): Promise<SharedLibHandler> {
  if (handler) {
    return handler;
  }

  if (!handlerPromise) {
    handlerPromise = new Promise((resolve, reject) => {
      handlerPromiseResolve = resolve;

      // Add timeout to prevent hanging indefinitely
      setTimeout(() => {
        reject(new Error('Handler initialization timeout - handler not set within 30 seconds'));
      }, 30000); // 20 second timeout
    });
  }

  return handlerPromise;
}

export function getRecordingController(): RecordingController {
  if (!handler) {
    throw new Error('Handler not set for sharedlib');
  }
  return handler.recordingController;
}
